import { FC } from 'react';
import { useIntl } from 'react-intl';
import { noop } from 'lodash';
import { useSessionStorage } from 'usehooks-ts';
import { faBookmark } from '@fortawesome/pro-duotone-svg-icons';

import { RecordsWithTimestamp } from '@app/components';
import { useAppContext } from '@app/contexts';
import {
  useFormattedSearches,
  UserSearchDto,
  useSavedInstancesFetch,
  useSavedSearchesFetch,
} from '@app/hooks';
import { sharedMessages } from '@app/translations';
import { Route, storageKeys } from '@app/constants';

import { HistoryRecordsPageWrapper } from '../HistoryRecordsPageWrapper';
import { useBatchInstancesFetch } from '../hooks/useBatchInstancesFetch';
import { DashboardSearchModalProvider } from '../DashboardSearchModal';

export const SavedPage: FC = () => {
  const { isLoggedIn } = useAppContext();
  const { $t } = useIntl();
  const formatSearches = useFormattedSearches();
  const [savedSearches] = useSessionStorage<UserSearchDto[]>(
    storageKeys.savedSearches,
    []
  );
  const [savedInstances] = useSessionStorage<RecordsWithTimestamp[]>(
    storageKeys.savedInstances,
    []
  );
  const savedRecordsQuery = useSavedInstancesFetch();
  const savedSearchesQuery = useSavedSearchesFetch();
  const savedRecordsDataQuery = useBatchInstancesFetch({
    queryKey: 'savedRecords',
    list: isLoggedIn ? savedRecordsQuery.data : savedInstances,
  });

  return (
    <DashboardSearchModalProvider>
      <HistoryRecordsPageWrapper
        pageTitle={$t(sharedMessages.saved)}
        parentRoute={Route.saved}
        searchesTabConfig={{
          title: $t({
            id: 'saved.searches',
            defaultMessage: 'Searches',
          }),
          documentTitle: $t({
            id: 'saved.savedSearchesPageTitle',
            defaultMessage: 'Searches - Saved - EBSCO Locate',
          }),
          route: Route.savedSearches,
          itemSubtitle: $t(sharedMessages.search),
          list: isLoggedIn
            ? savedSearchesQuery.data
            : formatSearches(savedSearches),
          isError: savedSearchesQuery.isError,
          isLoading: savedSearchesQuery.isInitialLoading,
          refetch: savedSearchesQuery.refetch,
          onEntryRemoval: noop,
          emptyStateMessage: $t({
            id: 'saved.emptyStateSearchMessage',
            defaultMessage: 'You currently have no saved searches.',
          }),
          emptyStateIcon: faBookmark,
          savedSearchesList: isLoggedIn
            ? savedSearchesQuery.data
            : formatSearches(savedSearches),
          isItemRemovable: true,
        }}
        recordsTabConfig={{
          title: $t({
            id: 'saved.records',
            defaultMessage: 'Records',
          }),
          documentTitle: $t({
            id: 'saved.savedRecordsPageTitle',
            defaultMessage: 'Records - Saved - EBSCO Locate',
          }),
          route: Route.savedRecords,
          list: savedRecordsDataQuery.data,
          isError: savedRecordsQuery.isError || savedRecordsDataQuery.isError,
          isLoading:
            savedRecordsQuery.isInitialLoading ||
            savedRecordsDataQuery.isInitialLoading,
          refetch: () =>
            savedRecordsQuery.isError
              ? savedRecordsQuery.refetch()
              : savedRecordsDataQuery.refetch(),
          onEntryRemoval: noop,
          emptyStateMessage: $t({
            id: 'saved.emptyStateRecordMessage',
            defaultMessage: 'You currently have no saved records.',
          }),
          emptyStateIcon: faBookmark,
          isItemRemovable: true,
        }}
        isSearchesTabFirst={false}
      />
    </DashboardSearchModalProvider>
  );
};
